import { useEffect, useState } from "react";
import { authStore } from "../auth";
import { onboardingStore } from "../auth/onboardingStore";
import { BillingBanner } from "../billing/BillingBanner";
import { billingStore } from "../billing/billingStore";
import {
  PLAY_SKU_MEMBER,
  PLAY_SKU_VIP,
  playBillingAvailable,
  purchasePlaySku,
  restorePlayPurchases,
} from "../billing/playBilling";
import { BrandMark } from "../components/BrandMark";
import { LanguagePicker, useT } from "../i18n";
import { VoiceAssistantFab } from "../voice/VoiceAssistantFab";
import {
  enablePushNotifications,
  pushSupported,
  readStoredPushToken,
} from "../push/pushClient";
import { commentStore } from "./commentStore";
import { MatchChat } from "./MatchCelebration";
import { FifaShieldClip } from "./FifaShieldClip";
import { dismissPackReveal, MatchFeed } from "./MatchFeed";
import { WhoLikedPage } from "./WhoLikedPage";
import { matchStore, profileFromAnswers } from "./matchStore";
import { resolveViewerGender, silentDemoGender } from "./resolveGender";
import type { MatchProfile } from "./types";
import { VerifyHost, VerifyStatusCard } from "../verify/VerifyHost";
import { verifyPaneFromHash } from "../verify/verifyGate";

type Tab = "selection" | "liked" | "messages" | "profile";

function chatIdFromHash(): string | null {
  const path = window.location.hash.replace(/^#\/?/, "");
  const m = path.match(/^match\/chat\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function tabFromHash(): Tab {
  const path = window.location.hash.replace(/^#\/?/, "");
  if (path.startsWith("match/chat/")) return "messages";
  if (path === "match/liked" || path.startsWith("match/liked/")) return "liked";
  if (path === "match/messages" || path.startsWith("match/messages/")) return "messages";
  if (path === "match/profile" || path.startsWith("match/profile/")) return "profile";
  return "selection";
}

function setTabHash(tab: Tab) {
  if (tab === "messages") window.location.hash = "#/match/messages";
  else if (tab === "liked") window.location.hash = "#/match/liked";
  else if (tab === "profile") window.location.hash = "#/match/profile";
  else window.location.hash = "#/match";
}

export function MatchApp() {
  const t = useT();
  const [tab, setTab] = useState<Tab>(() => tabFromHash());
  const [tick, setTick] = useState(0);
  const [chatId, setChatId] = useState<string | null>(() => chatIdFromHash());
  const [verifyPane, setVerifyPane] = useState<"id" | "video" | null>(() =>
    verifyPaneFromHash()
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [playNote, setPlayNote] = useState<string | null>(null);
  const [pushNote, setPushNote] = useState<string | null>(null);
  const [playBusy, setPlayBusy] = useState(false);
  const [booting, setBooting] = useState(true);
  const [signInOpen, setSignInOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const unsubMatch = matchStore.subscribe(() => setTick((n) => n + 1));
    const unsubAuth = authStore.subscribe(() => setTick((n) => n + 1));
    let cancelled = false;
    void (async () => {
      await authStore.refreshFromServer();
      if (cancelled) return;
      // Portfolio / stranger path: empty session → silent demo, no form.
      if (!authStore.getCurrentUser()) {
        const gender = silentDemoGender();
        const guest = authStore.continueAsGuest(gender);
        matchStore.enterAsGuest(gender, guest.id);
      }
      setBooting(false);
      setTick((n) => n + 1);
    })();
    return () => {
      cancelled = true;
      unsubMatch();
      unsubAuth();
    };
  }, []);

  // Seed demo inbound likes from an effect — never from getState/getDailyQueue render.
  useEffect(() => {
    matchStore.seedInbound();
  }, [tick]);

  useEffect(() => {
    const session = authStore.getSession();
    const selfId = matchStore.getState().self?.id ?? null;
    void billingStore.refresh(session?.userId ?? selfId);
  }, [tick]);

  useEffect(() => {
    const onHash = () => {
      setChatId(chatIdFromHash());
      setVerifyPane(verifyPaneFromHash());
      setTab(tabFromHash());
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const goTab = (next: Tab) => {
    if (next !== "selection") dismissPackReveal();
    setTab(next);
    setTabHash(next);
    setMenuOpen(false);
  };
  useEffect(() => {
    const user = authStore.getCurrentUser();
    const self = matchStore.getState().self;
    if (!user) return;
    if (self && self.id === user.id) return;

    if (user.id.startsWith("user_guest_")) {
      const gender = resolveViewerGender({ userId: user.id }) ?? silentDemoGender();
      matchStore.enterAsGuest(gender, user.id);
      return;
    }

    const saved = onboardingStore.load(user.id);
    const fromAnswers = saved
      ? profileFromAnswers(saved.answers, user.id, saved.profileAnswers)
      : null;
    const pool = matchStore.getProfile(user.id);
    const gender = resolveViewerGender({
      explicit: fromAnswers?.gender ?? pool?.gender,
      userId: user.id,
    });
    if (!gender) {
      setAuthError(t("match.needGender"));
      return;
    }
    const next: MatchProfile = fromAnswers
      ? fromAnswers
      : pool
        ? { ...pool, gender, source: "self" }
        : {
            id: user.id,
            firstName: (user.email.split("@")[0] || "You").trim(),
            age: 30,
            city: "—",
            country: undefined,
            preferredPartnerCountry: "any",
            gender,
            profession: "—",
            aboutYou: "Signed-in member.",
            lookingFor: "A serious Christian partner.",
            hobbies: "—",
            scales: { loyalty: 8, honesty: 8, passion: 7, commitment: 8 },
            source: "self",
          };
    matchStore.enterAsUser(next);
  }, [tick, t]);

  useEffect(() => {
    void matchStore.syncFromServer();
    const id = window.setInterval(() => {
      void matchStore.syncFromServer();
    }, 12_000);
    return () => window.clearInterval(id);
  }, []);

  const state = matchStore.getState();
  const self = state.self;
  const matches = matchStore.listMatches();
  const likedCount = state.likedIds.length;
  const inboundLikes = self ? matchStore.listInboundLikes() : [];
  const inboundCount = inboundLikes.length;
  /**
   * The ten-word opener that came with an inbound heart, if there was one.
   * Reads the comment addressed to the viewer FROM that person.
   */
  const inboundComment = (fromProfileId: string): string | null => {
    if (!self) return null;
    return commentStore.get(fromProfileId, self.id)?.text ?? null;
  };
  const user = authStore.getCurrentUser();
  const signedInMember = Boolean(user && !user.id.startsWith("user_guest_"));
  const memberMissingGender = Boolean(signedInMember && !self);
  const session = authStore.getSession();
  const isGuestSelf = Boolean(self?.id.startsWith("user_guest_"));
  const displayEmail =
    session?.email && !session.email.endsWith("@guest.un8.local")
      ? session.email
      : user?.email && !user.email.endsWith("@guest.un8.local")
        ? user.email
        : null;
  const playReady = playBillingAvailable();
  const pushOn = Boolean(readStoredPushToken());

  const runPlayPurchase = async (sku: typeof PLAY_SKU_MEMBER | typeof PLAY_SKU_VIP) => {
    setPlayNote(null);
    setPlayBusy(true);
    try {
      if (!playReady) {
        setPlayNote(t("billing.playUnavailable"));
        return;
      }
      setPlayNote(t("billing.playBusy"));
      const res = await purchasePlaySku(user?.id ?? self?.id ?? null, sku);
      if (res.ok) {
        setPlayNote(null);
        setTick((n) => n + 1);
        return;
      }
      if (res.error === "unavailable") setPlayNote(t("billing.playUnavailable"));
      else if (res.error === "cancelled") setPlayNote(null);
      else setPlayNote(res.detail || t("billing.playError"));
    } finally {
      setPlayBusy(false);
    }
  };

  const runPlayRestore = async () => {
    setPlayNote(null);
    setPlayBusy(true);
    try {
      if (!playReady) {
        setPlayNote(t("billing.playUnavailable"));
        return;
      }
      const res = await restorePlayPurchases(user?.id ?? self?.id ?? null);
      if (!res.ok) {
        setPlayNote(res.error === "unavailable" ? t("billing.playUnavailable") : res.error);
        return;
      }
      setTick((n) => n + 1);
    } finally {
      setPlayBusy(false);
    }
  };

  const runEnablePush = async () => {
    setPushNote(null);
    if (!pushSupported()) {
      setPushNote(t("billing.pushUnavailable"));
      return;
    }
    const res = await enablePushNotifications();
    if (res.ok) {
      setPushNote(
        res.mode === "webpush" ? t("billing.pushEnabled") : t("billing.pushUnavailable")
      );
      setTick((n) => n + 1);
      return;
    }
    if (res.reason === "denied") setPushNote(t("billing.pushBlocked"));
    else setPushNote(t("billing.pushUnavailable"));
  };

  const login = async () => {
    setAuthError(null);
    setBusy(true);
    try {
      const result = await authStore.login(email, password);
      if (!result.ok) {
        setAuthError(result.error);
        return;
      }
      const pool = matchStore.getProfile(result.user.id);
      const saved = onboardingStore.load(result.user.id);
      const fromAnswers = saved
        ? profileFromAnswers(saved.answers, result.user.id, saved.profileAnswers)
        : null;
      const gender = resolveViewerGender({
        explicit: fromAnswers?.gender ?? pool?.gender,
        userId: result.user.id,
      });
      if (!gender) {
        setAuthError(t("match.needGender"));
        window.location.hash = "#/";
        return;
      }
      if (fromAnswers) {
        matchStore.enterAsUser(fromAnswers);
      } else if (pool) {
        matchStore.enterAsUser({ ...pool, gender, source: "self" });
      } else {
        matchStore.enterAsUser({
          id: result.user.id,
          firstName: result.user.email.split("@")[0] || "You",
          age: 30,
          city: "—",
          country: undefined,
          preferredPartnerCountry: "any",
          gender,
          profession: "—",
          aboutYou: "Signed-in member.",
          lookingFor: "A serious Christian partner.",
          hobbies: "—",
          scales: { loyalty: 8, honesty: 8, passion: 7, commitment: 8 },
          source: "self",
        });
      }
      setSignInOpen(false);
      setTab("selection");
      setTabHash("selection");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    authStore.logout();
    billingStore.clearForLogout();
    matchStore.clearSession();
    // Stranger path again — silent demo, no login wall.
    const gender = silentDemoGender();
    const guest = authStore.continueAsGuest(gender);
    matchStore.enterAsGuest(gender, guest.id);
    setSignInOpen(false);
    window.location.hash = "#/match";
    setTick((n) => n + 1);
  };

  const openChat = (matchId: string) => {
    matchStore.unlockChat(matchId);
    window.location.hash = `#/match/chat/${encodeURIComponent(matchId)}`;
  };

  const closeChat = () => {
    window.location.hash = "#/match/messages";
    setTab("messages");
  };

  if (verifyPane) {
    const verifyUser = authStore.getCurrentUser()?.id ?? matchStore.getState().self?.id;
    if (verifyUser) {
      return (
        <div className="match-shell">
          <VerifyHost pane={verifyPane} userId={verifyUser} />
        </div>
      );
    }
  }

  if (chatId) {
    return (
      <div className="match-shell">
        <header className="match-top">
          <div className="match-brand">
            <BrandMark compact />
            <span className="match-brand__tag">{t("chat.brandTag")}</span>
          </div>
          <nav className="match-nav">
            <LanguagePicker compact />
            <button
              type="button"
              className="match-nav__link"
              onClick={closeChat}
            >
              {t("match.navMessages")}
            </button>
          </nav>
        </header>
        <main className="match-main match-main--docked">
          <MatchChat matchId={chatId} onBack={closeChat} />
        </main>
      </div>
    );
  }

  return (
    <div className="match-shell match-shell--app">
      <FifaShieldClip />
      <header className="match-top">
        <button
          type="button"
          className="match-brand match-brand--hit"
          onClick={() => setMenuOpen(true)}
          aria-label={t("match.menuOpen")}
        >
          <BrandMark compact />
        </button>
        {self ? <BillingBanner userId={user?.id ?? self.id} compact /> : null}
        <nav className="match-nav">
          <LanguagePicker compact />
        </nav>
      </header>

      {menuOpen ? (
        <div className="match-logo-menu" role="dialog" aria-label={t("match.menuOpen")}>
          <button
            type="button"
            className="match-logo-menu__veil"
            aria-label={t("match.menuClose")}
            onClick={() => setMenuOpen(false)}
          />
          <div className="match-logo-menu__bloom">
            <BrandMark hero />
            <button type="button" className="match-logo-menu__item" onClick={() => goTab("selection")}>
              {t("match.navSelection")}
            </button>
            <button type="button" className="match-logo-menu__item" onClick={() => goTab("liked")}>
              {t("match.navWhoLiked")}
              {inboundCount > 0 ? <span className="match-dock__badge">{inboundCount}</span> : null}
            </button>
            <button type="button" className="match-logo-menu__item" onClick={() => goTab("messages")}>
              {t("match.navMessages")}
            </button>
            <button type="button" className="match-logo-menu__item" onClick={() => goTab("profile")}>
              {t("match.navProfile")}
            </button>
            {isGuestSelf || (!signedInMember && self) ? (
              <button
                type="button"
                className="match-logo-menu__item"
                onClick={() => {
                  setMenuOpen(false);
                  setSignInOpen(true);
                }}
              >
                {t("match.quietSignIn")}
              </button>
            ) : null}
            <button
              type="button"
              className="match-logo-menu__item"
              onClick={() => {
                window.location.href =
                  "mailto:support@un8.app?subject=UN8%20beta%20feedback";
              }}
            >
              {t("welcome.reportBug")}
            </button>
            <button
              type="button"
              className="match-logo-menu__item"
              onClick={() => {
                try {
                  const locale = localStorage.getItem("un8.locale.v1");
                  localStorage.clear();
                  if (locale) localStorage.setItem("un8.locale.v1", locale);
                } catch {
                  /* ignore */
                }
                window.location.hash = "#/";
                window.location.reload();
              }}
            >
              {t("welcome.startFresh")}
            </button>
          </div>
        </div>
      ) : null}

      {signInOpen && (isGuestSelf || !signedInMember) ? (
        <div className="match-signin screen-enter" role="dialog" aria-label={t("match.quietSignIn")}>
          <p className="match-signin__hint">{t("match.signInHint")}</p>
          <div className="match-signin__form">
            <label className="field">
              <span className="field__label">{t("match.email")}</span>
              <input
                className="field__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("match.emailPlaceholder")}
                autoComplete="username"
              />
            </label>
            <label className="field">
              <span className="field__label">{t("match.password")}</span>
              <input
                className="field__input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("match.passwordPlaceholder")}
                autoComplete="current-password"
              />
            </label>
            {authError ? <p className="capture__error">{authError}</p> : null}
            <div className="match-signin__actions">
              <button
                type="button"
                className="match-act match-act--like"
                onClick={() => void login()}
                disabled={busy}
              >
                {busy ? t("common.ellipsis") : t("match.logIn")}
              </button>
              <button
                type="button"
                className="match-nav__link"
                onClick={() => setSignInOpen(false)}
              >
                {t("match.closeSignIn")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <main className="match-main match-main--docked">
        {booting && !self ? (
          <p className="match-list__sub match-boot">{t("match.bootDemo")}</p>
        ) : !self ? (
          <div className="match-gate screen-enter">
            {memberMissingGender ? (
              <>
                <h1>{t("match.enterTitle")}</h1>
                <p className="match-list__sub">{t("match.needGender")}</p>
                <a className="match-nav__link" href="#/">
                  {t("match.goOnboarding")}
                </a>
              </>
            ) : (
              <p className="match-list__sub match-boot">{t("match.bootDemo")}</p>
            )}
          </div>
        ) : (
          <>
            {tab === "selection" ? (
              <MatchFeed onOpenChat={openChat} />
            ) : null}

            {tab === "liked" ? (
              <WhoLikedPage onOpenChat={openChat} />
            ) : null}

            {tab === "messages" ? (
              <div className="match-list screen-enter">
                <h1>{t("match.yourMatches")}</h1>
                {self ? <VerifyStatusCard userId={user?.id ?? self.id} compact /> : null}
                <p className="match-list__sub">
                  {t("match.matchesSub", {
                    count: likedCount,
                    likesWord: likedCount === 1 ? t("match.like") : t("match.likes"),
                  })}
                </p>
                <section className="match-who" aria-label={t("match.inboundTitle")}>
                  <h2 className="match-who__title">{t("match.inboundTitle")}</h2>
                  {inboundLikes.length === 0 ? (
                    <p className="match-empty__meta">{t("match.whoLikedEmpty")}</p>
                  ) : (
                    <ul className="match-who__list">
                      {inboundLikes.map((p) => (
                        <li key={p.id}>
                          <strong>
                            {t("match.personLine", { name: p.firstName, age: p.age })}
                          </strong>
                          <span>{p.city}</span>
                          {inboundComment(p.id) ? (
                            <em className="match-who__comment">
                              “{inboundComment(p.id)}”
                            </em>
                          ) : null}
                          {/* Hearting BACK is deliberately free. The scarce act
                              is starting something, not answering someone who
                              already reached out — charging here would punish
                              exactly the behaviour that makes matches happen.
                              (Masco 2026-08-29.) */}
                          <button
                            type="button"
                            className="match-act match-act--like match-act--heart"
                            onClick={() => {
                              void matchStore.like(p.id).then((record) => {
                                if (record) openChat(record.id);
                              });
                            }}
                          >
                            {t("match.likeBack")}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                {matches.length === 0 ? (
                  <p className="match-list__empty">{t("match.emptyMatches")}</p>
                ) : (
                  <ul>
                    {matches.map((m) => {
                      const p = matchStore.getProfile(m.profileId);
                      if (!p) return null;
                      return (
                        <li key={m.id}>
                          <button
                            type="button"
                            className="match-list__row match-list__row--btn"
                            onClick={() => openChat(m.id)}
                          >
                            <div className="match-list__avatar" aria-hidden>
                              {p.firstName.charAt(0)}
                            </div>
                            <div className="match-list__copy">
                              <strong>
                                {t("match.personLine", { name: p.firstName, age: p.age })}
                              </strong>
                              <span>
                                {t("match.cityValues", { city: p.city, score: m.valuesScore })}
                                {m.chatUnlocked ? t("match.chatOpen") : t("match.tapToChat")}
                              </span>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {tab === "profile" ? (
              <div className="match-list screen-enter">
                <h1>{t("match.profileTitle")}</h1>
                <p className="match-list__sub">{t("match.profileSub")}</p>
                {self ? <VerifyStatusCard userId={user?.id ?? self.id} /> : null}
                <p className="match-list__sub">
                  {displayEmail
                    ? displayEmail
                    : isGuestSelf
                      ? t("match.guestSession")
                      : null}
                </p>
                <p className="match-list__sub">
                  {t("match.personLine", { name: self.firstName, age: self.age })} · {self.city} ·{" "}
                  {self.gender === "male" ? t("match.man") : t("match.woman")}
                </p>
                <div className="match-profile-billing">
                  <BillingBanner userId={user?.id ?? self.id} />
                </div>
                <div className="match-profile-actions">
                  {!isGuestSelf ? (
                    <>
                      {playReady ? (
                        <>
                          <button
                            type="button"
                            className="match-act match-act--like"
                            disabled={playBusy}
                            onClick={() => void runPlayPurchase(PLAY_SKU_MEMBER)}
                          >
                            {t("billing.playSubscribeMember")}
                          </button>
                          <button
                            type="button"
                            className="match-act match-act--like"
                            disabled={playBusy}
                            onClick={() => void runPlayPurchase(PLAY_SKU_VIP)}
                          >
                            {t("billing.playSubscribeVip")}
                          </button>
                          <button
                            type="button"
                            className="match-act match-act--pass"
                            disabled={playBusy}
                            onClick={() => void runPlayRestore()}
                          >
                            {t("billing.playRestore")}
                          </button>
                        </>
                      ) : (
                        <p className="match-list__sub">{t("billing.playUnavailable")}</p>
                      )}
                      {playNote ? <p className="match-empty__meta">{playNote}</p> : null}
                      <button
                        type="button"
                        className="match-act match-act--pass"
                        onClick={() => void runEnablePush()}
                      >
                        {pushOn ? t("billing.pushEnabled") : t("billing.enablePush")}
                      </button>
                      {pushNote ? <p className="match-empty__meta">{pushNote}</p> : null}
                      <button
                        type="button"
                        className="match-act match-act--like"
                        onClick={() =>
                          void billingStore.activateSubscription(user?.id ?? self.id)
                        }
                      >
                        {t("billing.activateSubscriberQa")}
                      </button>
                      <button
                        type="button"
                        className="match-act match-act--like"
                        onClick={() => void billingStore.activateVip(user?.id ?? self.id)}
                      >
                        {t("billing.activateVipQa")}
                      </button>
                    </>
                  ) : null}
                  {user ? (
                    <button type="button" className="match-act match-act--pass" onClick={logout}>
                      {t("match.logOut")}
                    </button>
                  ) : null}
                  <a className="match-nav__link" href="#/">
                    {t("match.navOnboarding")}
                  </a>
                  <a className="match-nav__link" href="#/review">
                    {t("match.navReview")}
                  </a>
                </div>
              </div>
            ) : null}
          </>
        )}
      </main>

      {self ? (
        <nav className="match-dock" aria-label="Main">
          <button
            type="button"
            className={`match-dock__item${tab === "selection" ? " is-active" : ""}`}
            onClick={() => goTab("selection")}
          >
            {t("match.navSelection")}
          </button>
          <button
            type="button"
            className={`match-dock__item${tab === "liked" ? " is-active" : ""}`}
            onClick={() => goTab("liked")}
          >
            {t("match.navWhoLiked")}
            {inboundCount > 0 ? (
              <span className="match-dock__badge" aria-label={t("match.inboundTitle")}>
                {inboundCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className={`match-dock__item${tab === "messages" ? " is-active" : ""}`}
            onClick={() => goTab("messages")}
          >
            {t("match.navMessages")}
          </button>
          <button
            type="button"
            className={`match-dock__item${tab === "profile" ? " is-active" : ""}`}
            onClick={() => goTab("profile")}
          >
            {t("match.navProfile")}
          </button>
        </nav>
      ) : null}
      <VoiceAssistantFab matchDock={Boolean(self)} />
    </div>
  );
}
